import { useState } from "react";
import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AdminHealthBanner from "@/components/admin/AdminHealthBanner";
import {
  LayoutDashboard, Library, BookMarked, ExternalLink, LogOut, Landmark,
  Eye, Settings, Inbox, HeartHandshake, Newspaper, Users, Boxes, CalendarCheck,
  UploadCloud, Menu, X, Languages as LanguagesIcon, ArrowRightLeft, Images,
  Image as ImageIcon, CalendarDays, Sparkles, MessageSquare, CalendarClock, Megaphone, Database, Music,
  Activity, Type, ListChecks, ScrollText, Eraser, Trash2, Tags, Bell, BookOpen, Mail, Network, FlaskConical, MapPin,
  ShieldCheck, Smartphone, CreditCard,
} from "lucide-react";

const NAV = [
  { group: null, items: [
    { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" },
    { to: "/admin/traffic", icon: Activity, label: "Site Analytics" },
    { to: "/admin/activity", icon: Activity, label: "Activity Log" },
    { to: "/admin/prd", icon: ScrollText, label: "Product Notes (PRD)" },
  ]},
  { group: "Content", items: [
    { to: "/admin/content", end: true, icon: Library, label: "Content" },
    { to: "/admin/taxonomy", icon: Tags, label: "Taxonomy" },
    { to: "/admin/glossary", icon: BookMarked, label: "Glossary" },
    { to: "/admin/sources", icon: BookMarked, label: "Sources & References" },
    { to: "/admin/ebooks", icon: BookOpen, label: "eBooks & Manuscripts" },
    { to: "/admin/amnaya", icon: Landmark, label: "Amnaya Peethas" },
    { to: "/admin/journal", icon: Newspaper, label: "Patrika (Blog)" },
    { to: "/admin/changelog", icon: Megaphone, label: "What's New" },
    { to: "/admin/import", icon: UploadCloud, label: "Bulk Import" },
    { to: "/admin/data", icon: Database, label: "Data & Health" },
    { to: "/admin/cleanup", icon: Eraser, label: "Data Sanitize" },
    { to: "/admin/trash", icon: Trash2, label: "Trash" },
  ]},
  { group: "Media & Audio", items: [
    { to: "/admin/media", icon: Images, label: "Media Library" },
    { to: "/admin/audios", icon: Music, label: "Audio Library & Live" },
    { to: "/admin/homepage", icon: ImageIcon, label: "Homepage Images" },
    { to: "/admin/temples", icon: Landmark, label: "Temple Images & Deities" },
    { to: "/admin/devi-images", icon: Sparkles, label: "Devi Images" },
  ]},
  { group: "Festivals & Panchang", items: [
    { to: "/admin/festivals", icon: CalendarDays, label: "Festival Calendar" },
    { to: "/admin/festival-pages", icon: BookOpen, label: "Festival Pages" },
    { to: "/admin/home-festivals", icon: CalendarClock, label: "Homepage Festivals" },
    { to: "/admin/panchang", icon: CalendarDays, label: "Panchangam Editor" },
    { to: "/admin/premium", icon: Sparkles, label: "Panchangam Premium" },
    { to: "/admin/panchang-lab", icon: FlaskConical, label: "Panchangam Test Engine" },
    { to: "/admin/panchang-cities", icon: MapPin, label: "City Panchangam Pages" },
    { to: "/admin/festival-muhurta", icon: CalendarDays, label: "Festival Muhurta Pages" },
  ]},
  { group: "Community & People", items: [
    { to: "/admin/community", icon: MessageSquare, label: "Community Forum" },
    { to: "/admin/events", icon: CalendarClock, label: "Events" },
    { to: "/admin/announcements", icon: Megaphone, label: "Announcements" },
    { to: "/admin/waitlist", icon: ListChecks, label: "Waitlist" },
    { to: "/admin/users", icon: Users, label: "Users & Roles" },
    { to: "/admin/messages", icon: Inbox, label: "Messages" },
    { to: "/admin/feedback", icon: MessageSquare, label: "Feedback" },
    { to: "/admin/donations", icon: HeartHandshake, label: "Donations" },
  ]},
  { group: "Services & Bookings", items: [
    { to: "/admin/services", icon: Boxes, label: "Services" },
    { to: "/admin/professionals", icon: ShieldCheck, label: "Professionals" },
    { to: "/admin/bookings", icon: CalendarCheck, label: "Bookings" },
  ]},
  { group: "Messaging & Campaigns", items: [
    { to: "/admin/email", icon: Mail, label: "Email & Campaigns" },
    { to: "/admin/mobile-auth", icon: Smartphone, label: "Mobile Sign-in Keys" },
    { to: "/admin/integrations", icon: CreditCard, label: "Payments & Push" },
    { to: "/admin/whatsapp", icon: MessageSquare, label: "WhatsApp" },
    { to: "/admin/zoho-campaigns", icon: Megaphone, label: "Zoho Campaigns" },
    { to: "/admin/system-notifications", icon: Bell, label: "System Notifications" },
  ]},
  { group: "Site, Pages & SEO", items: [
    { to: "/admin/site-pages", icon: ScrollText, label: "Pages & SEO" },
    { to: "/admin/spotlight", icon: Sparkles, label: "Daily Spotlight" },
    { to: "/admin/typography", icon: Type, label: "Typography" },
    { to: "/admin/languages", icon: LanguagesIcon, label: "Languages & Translations" },
    { to: "/admin/mappings", icon: Network, label: "Mappings" },
    { to: "/admin/settings", icon: Settings, label: "Settings" },
    { to: "/admin/sync", icon: ArrowRightLeft, label: "Sync (Preview → Live)" },
  ]},
];

const testid = (to) => `admin-nav-${to.replace(/\//g, "-").replace(/^-/, "")}`;

// Which permission each sidebar destination needs. null = any privileged staff;
// destinations not listed here are admin-only.
const NAV_PERM = {
  "/admin": null,
  "/admin/activity": "site.manage",
  "/admin/content": "content.edit",
  "/admin/content/new": "content.create",
  "/admin/data": "content.edit",
  "/admin/media": "media.manage",
  "/admin/collections": "content.edit",
  "/admin/ebooks": "content.edit",
  "/admin/festival-pages": "content.edit",
  "/admin/home-festivals": "site.manage",
  "/admin/taxonomy": "content.edit",
  "/admin/typography": "content.edit",
  "/admin/system-notifications": "content.edit",
  "/admin/glossary": "content.edit",
  "/admin/journal": "content.edit",
  "/admin/changelog": "content.edit",
  "/admin/import": "content.create",
  "/admin/community": "community.moderate",
  "/admin/events": "community.moderate",
  "/admin/announcements": "community.moderate",
  "/admin/waitlist": "site.manage",
  "/admin/users": "users.manage",
  "/admin/messages": "site.manage",
  "/admin/feedback": "site.manage",
  "/admin/audios": "site.manage",
  "/admin/donations": "site.manage",
  "/admin/services": "site.manage",
  "/admin/professionals": "site.manage",
  "/admin/mobile-auth": "site.manage",
  "/admin/integrations": "site.manage",
  "/admin/bookings": "site.manage",
  "/admin/homepage": "site.manage",
  "/admin/temples": "site.manage",
  "/admin/devi-images": "site.manage",
  "/admin/panchang": "site.manage",
  "/admin/panchang-cities": "site.manage",
  "/admin/festival-muhurta": "site.manage",
  "/admin/visibility": "site.manage",
  "/admin/headings": "site.manage",
  "/admin/spotlight": "site.manage",
  "/admin/navigation": "site.manage",
  "/admin/languages": "site.manage",
  "/admin/translations": "site.manage",
  "/admin/seo": "site.manage",
  "/admin/pages": "site.manage",
  "/admin/site-pages": "site.manage",
  "/admin/legal": "site.manage",
  "/admin/settings": "site.manage",
  "/admin/mappings": "content.manage",
  "/admin/premium": "site.manage",
  "/admin/email": "site.manage",
  "/admin/whatsapp": "site.manage",
  "/admin/zoho-campaigns": "site.manage",
  "/admin/sync": "site.manage",
};
const PRIV_ROLES = ["admin", "content_editor", "moderator", "guest_writer"];

export default function AdminLayout() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!ready) return <div className="flex min-h-screen items-center justify-center bg-parchment text-ink-3">…</div>;
  if (!user || !PRIV_ROLES.includes(user.role)) return <Navigate to="/admin/login" replace />;

  const isAdmin = user.role === "admin";
  const perms = user.permissions || [];
  const can = (to) => {
    if (isAdmin) return true;
    const p = NAV_PERM[to];
    if (p === undefined) return false; // admin-only
    if (p === null) return true;
    return perms.includes(p);
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
      isActive ? "bg-[rgba(199,80,0,0.1)] text-saffron" : "text-ink-2 hover:bg-surface-alt hover:text-ink"
    }`;

  const NavItems = ({ onNavigate, scope = "" }) => (
    <>
      {NAV.map((section, i) => {
        const items = section.items.filter((it) => can(it.to));
        if (items.length === 0) return null;
        return (
          <div key={i} className={section.group ? "mt-5" : ""}>
            {section.group && <p className="px-4 pb-1.5 text-[10px] uppercase tracking-[0.14em] text-ink-3/70">{section.group}</p>}
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} data-testid={`${scope}${testid(it.to)}`} onClick={onNavigate} className={linkCls}>
                <it.icon className="h-4 w-4" /> {it.label}
              </NavLink>
            ))}
          </div>
        );
      })}
    </>
  );

  return (
    <div className="flex min-h-screen bg-parchment">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-line bg-surface md:flex">
        <div className="border-b border-line px-6 py-6">
          <p className="font-deva text-3xl text-ink">धर्मार्थ</p>
          <p className="mt-1 overline-label text-gold">Console</p>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          <NavItems />
          <div className="mt-5 border-t border-line pt-3">
            <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-2 transition-colors hover:bg-surface-alt hover:text-ink"><ExternalLink className="h-4 w-4" /> View site</a>
          </div>
        </nav>
        <div className="border-t border-line p-4">
          <p className="mb-2 px-1 text-xs text-ink-3">{user.email}</p>
          <button data-testid="admin-logout" onClick={doLogout} className="flex w-full items-center gap-3 px-1 py-2 text-sm text-ink-2 transition-colors hover:text-saffron">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 overflow-x-hidden">
        {/* Mobile top bar */}
        <div className="flex items-center justify-between border-b border-line bg-surface px-5 py-4 md:hidden">
          <span className="font-deva text-2xl text-ink">धर्मार्थ</span>
          <button data-testid="admin-mobile-menu" onClick={() => setMobileOpen(true)} className="inline-flex items-center gap-2 text-sm text-ink-2">
            <Menu className="h-5 w-5" /> Menu
          </button>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="absolute inset-0 bg-ink/40" onClick={() => setMobileOpen(false)} />
            <div className="absolute left-0 top-0 flex h-full w-72 max-w-[85%] flex-col bg-surface shadow-xl">
              <div className="flex items-center justify-between border-b border-line px-5 py-4">
                <span className="overline-label text-gold">Console</span>
                <button data-testid="admin-mobile-close" onClick={() => setMobileOpen(false)} className="text-ink-2"><X className="h-5 w-5" /></button>
              </div>
              <nav className="flex-1 overflow-y-auto py-4">
                <NavItems scope="m-" onNavigate={() => setMobileOpen(false)} />
                <div className="mt-5 border-t border-line pt-3">
                  <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-2 hover:text-ink"><ExternalLink className="h-4 w-4" /> View site</a>
                </div>
              </nav>
              <div className="border-t border-line p-4">
                <p className="mb-2 px-1 text-xs text-ink-3">{user.email}</p>
                <button onClick={doLogout} className="flex w-full items-center gap-3 px-1 py-2 text-sm text-ink-2 hover:text-saffron">
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="p-5 md:p-8">
          {isAdmin && <AdminHealthBanner />}
          <Outlet />
        </div>
      </div>
    </div>
  );
}
